import { Command, Keyboard, X } from 'lucide-react';
import { useEffect } from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: 'viewer' | 'dashboard';
}

export function HelpModal({ isOpen, onClose, context = 'viewer' }: HelpModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-brand-border bg-brand-surface shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-brand-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-dark text-brand-orange">
              <Keyboard className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Keyboard Shortcuts</h2>
              <p className="text-xs text-brand-text-secondary">Master the workflow</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-brand-text-secondary hover:bg-brand-surface-highlight hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {context === 'dashboard' ? (
            <div className="grid gap-4">
              <ShortcutRow keys={['R']} description="Refresh dashboard data" />
              <ShortcutRow keys={['Enter']} description="Start annotating (Random)" />
              <ShortcutRow keys={['/']} description="Focus history search" />
            </div>
          ) : (
            <>
              <div className="grid gap-4">
                <ShortcutRow
                  keys={['1', '2', '3', '4']}
                  altKeys={['A', 'S', 'D', 'F']}
                  description="Toggle selection for frames in current batch"
                />
                <ShortcutRow
                  keys={['K']}
                  altKeys={['Shift + J']}
                  description="Next batch (Commits on last batch)"
                />
                <ShortcutRow keys={['5', 'J']} description="Previous batch" />
                <ShortcutRow keys={['Enter']} description="Toggle all frames in current batch" />
                <ShortcutRow keys={['←', '→']} description="Shift window by 1 frame" />
                <ShortcutRow keys={['0']} description="Jump to previous New Action (TAS only)" />
                <ShortcutRow keys={['V']} description="Jump to previous unvisited" />
                <ShortcutRow keys={['N']} description="Jump to next unvisited" />
              </div>

              <div className="rounded-xl bg-brand-dark/50 p-4 border border-brand-border/50">
                <div className="flex items-start gap-3">
                  <Command className="h-5 w-5 text-brand-text-secondary mt-0.5" />
                  <div className="text-sm text-brand-text-secondary">
                    <p className="mb-1">
                      <span className="text-white font-medium">Pro Tip:</span> Use{' '}
                      <span className="font-mono text-brand-orange">Space</span> to rapidly move
                      through batches. It will automatically save your work when you reach the end.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bg-brand-surface-highlight/30 px-6 py-4 text-center">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-brand-surface border border-brand-border py-2 text-sm font-medium text-brand-text-primary hover:bg-brand-surface-highlight hover:text-white transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function ShortcutRow({
  keys,
  altKeys,
  description,
}: {
  keys: string[];
  altKeys?: string[];
  description: string;
}) {
  return (
    <div className="flex items-center justify-between group">
      <span className="text-sm font-medium text-brand-text-secondary group-hover:text-brand-text-primary transition-colors">
        {description}
      </span>
      <div className="flex flex-col items-end gap-1">
        <div className="flex gap-1.5">
          {keys.map((k) => (
            <kbd
              key={k}
              className="min-w-[1.5rem] rounded bg-brand-dark border border-brand-border px-1.5 py-0.5 text-center font-mono text-xs font-bold text-white shadow-sm"
            >
              {k}
            </kbd>
          ))}
        </div>
        {altKeys && (
          <div className="flex gap-1.5 opacity-50">
            {altKeys.map((k) => (
              <kbd
                key={k}
                className="min-w-[1.5rem] rounded bg-brand-dark border border-brand-border px-1.5 py-0.5 text-center font-mono text-[10px] text-white shadow-sm"
              >
                {k}
              </kbd>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
